import { ClientInformationProgressWrapper as ClientInformationProgress } from "../ClientInformationProgress";
import { ClientInformationFormsAndQuestionnaires } from "../ClientInformationFormsAndQuestionnaires";
import { useAtomValue, useSetAtom } from "jotai";
import { CombinedFormDataEntriesByFormTitle } from "../types";
import { useGetFormsByPatientShortId } from "../../useGetFormsByPatientShortId";
import {
  ProviderDrawerState,
  isInSessionWithSelectedClientAtom,
  treatmentPlanContentStatus,
  drawerStateAtom,
  selectedClientInfoAtom,
  selectedPatientId,
} from "../../state";
import { ClinicalConsiderationsSectionWrapper as ClinicalConsiderationsSection } from "../ClinicalConsiderationsSection";
import {
  TreatmentPlansOverviewConnected,
  TreatmentPlanStatus,
  TreatmentPlansOverviewConnectedProps,
  NudgeObject,
} from "@grow-therapy-team/seedling-components";
import { Card } from "@growtherapy/sprout-ui";
import {
  completedFormsForSelectedClientAtom,
  hasNewFormsAtomFamily,
} from "../state";
import { EventTypeEnum } from "../../../segment/types";
import { useSendClientInfoTabViewTrackingEvent } from "../useSendClientInfoTabViewTrackingEvent";

interface TreatmentTabProps {
  patientShortId: string;
  completedForms?: CombinedFormDataEntriesByFormTitle;
  hasNewForms?: boolean;
  nudges: NudgeObject[];
  patientId: string;
  shouldShowFormsSection?: boolean;
  isInSessionWithSelectedClient: boolean;
  shouldShowClinicalConsiderationSection: boolean;
  onClickTreatmentPlans: (treatmentPlanStatus: TreatmentPlanStatus) => void;
  treatmentPlansComponent: React.FC<TreatmentPlansOverviewConnectedProps>;
  clientInformationProgress: React.FC;
}

export function TreatmentTab({
  completedForms,
  hasNewForms,
  patientId,
  patientShortId,
  shouldShowFormsSection,
  onClickTreatmentPlans,
  isInSessionWithSelectedClient,
  shouldShowClinicalConsiderationSection,
  nudges,
  treatmentPlansComponent: TreatmentPlans,
  clientInformationProgress: ClientInformationProgress,
}: TreatmentTabProps) {
  return (
    <>
      {shouldShowClinicalConsiderationSection && (
        <ClinicalConsiderationsSection
          nudges={nudges}
          patientId={patientId}
          isInSessionWithSelectedClient={isInSessionWithSelectedClient}
        />
      )}
      <div data-testid="client-info.treatment.progress">
        <ClientInformationProgress />
      </div>
      {shouldShowFormsSection && (
        <Card>
          <ClientInformationFormsAndQuestionnaires
            completedForms={completedForms}
            hasNewForms={hasNewForms}
          />
        </Card>
      )}
      <div className="pt-4" data-testid="client-info.treatment.treatment-plans">
        <TreatmentPlans
          patientShortId={patientShortId}
          onClick={() =>
            onClickTreatmentPlans(TreatmentPlanStatus.CURRENT_GOALS)
          }
        />
      </div>
    </>
  );
}

export function TreatmentTabWrapper() {
  const selectedClientInfo = useAtomValue(selectedClientInfoAtom);
  const patientShortId = selectedClientInfo?.patientShortId ?? "";
  useGetFormsByPatientShortId(patientShortId);
  const completedForms = useAtomValue(completedFormsForSelectedClientAtom);
  const nudges = selectedClientInfo?.nudges ?? [];
  const hasNewForms = useAtomValue(hasNewFormsAtomFamily(patientShortId));
  const patientId = useAtomValue(selectedPatientId) ?? "";

  const setDrawerState = useSetAtom(drawerStateAtom);
  const setTreatmentPlanContentStatus = useSetAtom(treatmentPlanContentStatus);
  const onClickTreatmentPlans = (treatmentPlanStatus: TreatmentPlanStatus) => {
    setTreatmentPlanContentStatus(treatmentPlanStatus);
    setDrawerState(ProviderDrawerState.TREATMENT_PLANS_CONTENT);
  };

  const hasCompletedForms =
    completedForms && Object.values(completedForms).length > 0;
  const shouldShowClinicalConsiderationSection = !!nudges.length;

  const isInSessionWithSelectedClient = useAtomValue(
    isInSessionWithSelectedClientAtom,
  );
  const shouldShowFormsSection =
    isInSessionWithSelectedClient || hasCompletedForms;

  useSendClientInfoTabViewTrackingEvent(
    patientShortId,
    EventTypeEnum.TREATMENT_TAB,
  );

  return (
    <TreatmentTab
      hasNewForms={hasNewForms}
      nudges={nudges}
      completedForms={completedForms}
      patientShortId={patientShortId}
      onClickTreatmentPlans={onClickTreatmentPlans}
      shouldShowFormsSection={shouldShowFormsSection}
      shouldShowClinicalConsiderationSection={
        shouldShowClinicalConsiderationSection
      }
      patientId={patientId}
      isInSessionWithSelectedClient={isInSessionWithSelectedClient}
      treatmentPlansComponent={TreatmentPlansOverviewConnected}
      clientInformationProgress={ClientInformationProgress}
    />
  );
}
